'use client';

import React from 'react';
import { OrderWay, type OrderWayProps } from '@pixum/orderway';
import { Image, IMAGE_LOADING } from '../../image';
import { RichTextBody } from '../../richtText';
import type { LinkNode, CardNode } from 'data-layer';
import { LINK_TYPES } from 'data-layer/constants';
import { isLinkOut, isLinkType, nth, trackEvent, getBaseUrl } from 'utils';
import { trackLinkOut } from '../../../utils/analytics';
import { OrderWayActions } from './OrderWayActions';
import { ButtonVariants } from '@pixum/button';
import { CREATIVE_ID, useAppAdjustLink } from 'hooks';
import { type LinkProps, withLinkMasking, type WrappedComponent } from '../../link';
import type { DeviceOrientedComponentProps } from '../../../types';

interface OrderWayCardProps extends DeviceOrientedComponentProps {
  card: CardNode;
  inDialog?: boolean;
  isFocused?: boolean;
}

type MouseEventType = React.MouseEvent;

const OrderWayCardWithLinkMasking = withLinkMasking<OrderWayProps & LinkProps>(
  OrderWay as WrappedComponent<OrderWayProps>
);

/**
 * OrderWayCard Component
 *
 * This component renders an OrderWay card with dynamic content, image, and actions.
 * It is designed to be responsive and adaptable to different contexts such as mobile views,
 * dialog presentations, and focus states.
 *
 * Props:
 * - `card` (CardNode): The data object containing information for the card, including headline, text, images, and CTAs.
 * - `isMobile` (boolean, optional): Indicates whether the component is being rendered in a mobile view.
 * - `inDialog` (boolean, optional): Specifies if the card is displayed within a dialog.
 * - `isFocused` (boolean, optional): Determines if the card is currently focused.
 *
 * Behavior:
 * - Dynamically selects the appropriate image and CTA link based on the `isMobile` prop.
 * - Adjusts the CTA button variant based on `inDialog` and `isFocused` states.
 * - Tracks external link clicks and opens links in the current browser tab/window.
 * - Updates the `adjustLink` state based on the mobile view and app-adjusted link.
 *
 * Edge Cases:
 * - If no CTA link is provided, the `ctaLink` will default to an empty string.
 * - If the `isMobile` prop is true but no mobile-specific image or CTA is available, it falls back to desktop values.
 * - External links are tracked only if they differ from the current origin.
 * - Prevents default browser behavior for CTA clicks to ensure custom handling.
 */
export function OrderWayCard({ card, device, inDialog, isFocused }: OrderWayCardProps) {
  const { id, headline, links, image } = card;

  const content = <RichTextBody text={card.text} />;
  const link = nth(links, 0);
  const ctaLink = link && link.url ? (link.url as string) : '';

  const appAdjustLink = useAppAdjustLink(ctaLink, CREATIVE_ID.CONTENT);

  const ctaVariant =
    inDialog && isFocused
      ? ButtonVariants.STRONG
      : inDialog && !isFocused
        ? ButtonVariants.SECONDARY
        : ButtonVariants.TERTIARY;

  /**
   * Handles the click event for the primary call-to-action (CTA) button.
   *
   * Functionality:
   * - Prevents the default browser behavior for the click event.
   * - Tracks the click event based on the link type (online designer, software, app, or external link).
   * - Opens the link in the current browser tab or window using `window.open`.
   *
   * Parameters:
   * - `e` (MouseEventType): The mouse event triggered by the click.
   * - `url` (string, optional): The URL to be opened. If not provided, no action is taken.
   */
  const onCtaClick = (e: MouseEventType, url?: string) => {
    if (!url || !link) return;

    // Track based on the link type
    if (isLinkType(link, LINK_TYPES.ONLINE_DESIGNER)) {
      trackEvent('Fotobuch', 'Bestellwege', 'Online-Designer');
    } else if (isLinkType(link, LINK_TYPES.DOWNLOAD_SOFTWARE)) {
      // Get OS name for software download tracking
      const osName = typeof navigator !== 'undefined' ? navigator.platform : 'unknown';
      trackEvent('Download', 'Fotobuch', osName);
    } else if (isLinkType(link, LINK_TYPES.DOWNLOAD_APP_DEEPLINK)) {
      trackEvent('Fotobuch', 'Bestellwege', 'Pixum-App');
    } else {
      // Track external links
      const isOutsideLink = isLinkOut(url, getBaseUrl());

      if (isOutsideLink) {
        trackLinkOut(url);
      }
    }
  };

  // masked: true (SEO requirement PXMCY-16888): all links in the OrderWay module must be masked
  return (
    <OrderWayCardWithLinkMasking
      action={
        <OrderWayActions
          actions={link ? [{ ...link, masked: true } as LinkNode] : []}
          ctaVariant={ctaVariant}
          device={device}
        />
      }
      content={content}
      focus={isFocused}
      image={<Image image={image} loading={IMAGE_LOADING.LAZY} device={device} />}
      inDialog={inDialog}
      key={id}
      headline={headline as string}
      href={appAdjustLink}
      onClick={(e: MouseEventType) => onCtaClick(e, appAdjustLink)}
      masked={true}
    />
  );
}
